import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';

import { type Subscription, timer } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { BaseStateService } from '@core/services/base-state.service';
import { ModalService } from '@core/services/modal.service';

import { IframeModal } from '@shared/modals/iframe/iframe.modal';
import { DataState } from '@shared/utils/data-state';

import {
  OrderInfoService,
  type OrderPaymentDeepLinkDto,
  type SubscriptionDto,
  SubscriptionService,
} from '@swagger-services/order';
import {
  type CardWalletDto,
  PersonaPayDeepLinkService,
  PersonaPayWalletService,
  type WalletControllerFind200ResponseInner,
} from '@swagger-services/payments';

export interface WalletData {
  wallet: CardWalletDto | undefined;
  subscription: SubscriptionDto;
}

@Injectable()
export class WalletService extends BaseStateService<WalletData> {
  private readonly personaPayWalletService = inject(PersonaPayWalletService);
  private readonly personaPayDeepLinkService = inject(PersonaPayDeepLinkService);
  private readonly subscriptionService = inject(SubscriptionService);
  private readonly orderInfoService = inject(OrderInfoService);
  private readonly modalService = inject(ModalService);
  private readonly translateService = inject(TranslateService);

  private pollingSubscription?: Subscription;

  private readonly _initialSubscription = signal<SubscriptionDto | undefined>(undefined);

  readonly wallet = computed(() => this.state().getData()?.wallet);
  readonly subscription = computed(() => this.state().getData()?.subscription ?? this._initialSubscription());

  constructor() {
    super();
    inject(DestroyRef).onDestroy(() => this.pollingSubscription?.unsubscribe());
  }

  loadWallet(subscription: SubscriptionDto): Promise<void> {
    this._initialSubscription.set(subscription);
    return this.load(async () => {
      const wallets = await this.personaPayWalletService.walletControllerFind('');
      const wallet = this.findActiveWallet(wallets, subscription);
      return { wallet, subscription };
    });
  }

  async updatePaymentMethod(): Promise<void> {
    const subscription = this.subscription();
    if (!subscription) return;

    const subscriptionId = subscription.id;
    let fetchUrl = async () => {
      const res = await this.personaPayDeepLinkService.deepLinkControllerWallet('');
      await this.subscriptionService.subscriptionControllerUpdate('', subscriptionId, {
        metadata: { webTokenId: res.webTokenId, consumer: res.consumer },
      });
      return res.webTokenUrl;
    };

    const orderId = subscription.error?.orderId;
    if (orderId) {
      fetchUrl = async () => {
        const res: OrderPaymentDeepLinkDto = await this.orderInfoService.orderInfoControllerGetPaymentDeepLink(
          '',
          orderId,
        );
        return res.data.webTokenUrl;
      };
    }

    const modalClosed = this.modalService.openModal(IframeModal, {
      title: this.translateService.instant('iframe.updatePaymentMethod'),
      fetchUrl,
    });

    this.startPolling(modalClosed);
  }

  private startPolling(modalClosed: Promise<unknown>): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = timer(0, 5000).subscribe(() => {
      void this.refresh();
    });

    void modalClosed.then(() => {
      this.pollingSubscription?.unsubscribe();
      void this.refresh();
    });
  }

  private async refresh(): Promise<void> {
    const currentSubscription = this.subscription();
    if (!currentSubscription) return;

    try {
      const subscription = await this.subscriptionService.subscriptionControllerFindOne('', currentSubscription.id);
      const wallets = await this.personaPayWalletService.walletControllerFind('');
      const wallet = this.findActiveWallet(wallets, subscription);
      this.state.set(DataState.success({ wallet, subscription }));
    } catch {
      // Silently fail during polling - don't disrupt user experience
    }
  }

  private findActiveWallet(
    wallets: WalletControllerFind200ResponseInner[],
    subscription: SubscriptionDto,
  ): CardWalletDto | undefined {
    const walletId = subscription.metadata.walletId;

    if (walletId) {
      const found = wallets.find((w) => w.externalId === walletId);
      return found && 'cardType' in found ? found : undefined;
    }

    const cardWallet = wallets.find((w) => 'cardType' in w);
    return cardWallet as CardWalletDto | undefined;
  }
}
